import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { APP_CONFIG, ADVANCED_CONFIG, AUTH_CONFIG, BILLING_CONFIG, REPORT_CONFIG } from '../config';
import { useAuth, useGuestMode } from '../lib/auth';
import { useBilling, useUsageTracking } from '../lib/billing';
import { api } from '../utils/api';
import { t } from '../utils/i18n';
import { isRuntimePaymentRequired } from '../utils/runtimeError';
import CompletionForm from './CompletionForm';
import ResultDisplay from './ResultDisplay';
import ErrorAlert from './ErrorAlert';
import UserMenu from './auth/UserMenu';
import GuestPrompt from './auth/GuestPrompt';

const CompletionApp = () => {
  const [result, setResult] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const visitTrackedRef = useRef(false);

  // Auth hooks
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { canUseAsGuest, incrementUsage: incrementGuestUsage } = useGuestMode();

  // Billing hooks
  const { currentPlan, isEnabled: billingEnabled } = useBilling();
  const { incrementUsage, getQuotaInfo } = useUsageTracking();

  // Get quota info
  const quotaInfo = getQuotaInfo(currentPlan);

  // Check URL for auth error
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('error') === 'auth_failed') {
      setError('Authentication failed. Please try again.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Track first page visit after auth state settles.
  useEffect(() => {
    if (!authLoading && !visitTrackedRef.current) {
      visitTrackedRef.current = true;
      void api.warmRuntimeConfig();
      void api.trackFunnelEvent('app.visit', {
        userId: user?.id,
        metadata: {
          auth_enabled: AUTH_CONFIG.ENABLED,
          billing_enabled: BILLING_CONFIG.ENABLED,
        },
      });
    }
  }, [authLoading, user?.id]);

  // Clear error messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, ADVANCED_CONFIG.ERROR_DISPLAY_DURATION);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear copied state
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // Handle proceeding as guest
  const handleProceedAsGuest = () => {
    setIsGuestMode(true);
    setShowGuestPrompt(false);
  };

  // Handle form submission
  const handleSubmit = async (payload) => {
    try {
      // Validate configuration
      api.validateConfig();

      // Check quota and authentication
      if (isAuthenticated) {
        // Authenticated user - check subscription quota
        if (currentPlan === 'free' && !quotaInfo.canUse) {
          void api.trackFunnelEvent('quota.exhausted', {
            userId: user?.id,
            metadata: {
              auth_state: 'authenticated',
              daily_remaining: quotaInfo.dailyRemaining,
              monthly_remaining: quotaInfo.monthlyRemaining,
            },
          });
          if (BILLING_CONFIG.ENABLED) {
            void api.trackFunnelEvent('paid.gate.shown', {
              userId: user?.id,
              metadata: { reason: 'quota_exhausted' },
            });
          }
          setError(t('quotaExceeded'));
          return;
        }
      } else if (isGuestMode) {
        // Guest mode - check guest quota
        if (!canUseAsGuest) {
          void api.trackFunnelEvent('quota.exhausted', {
            metadata: { auth_state: 'guest' },
          });
          setError(t('guestQuotaExceeded'));
          setShowGuestPrompt(true);
          void api.trackFunnelEvent('login.prompt.shown', {
            metadata: { reason: 'guest_quota_exhausted' },
          });
          return;
        }
      } else {
        // Not authenticated and not in guest mode
        setShowGuestPrompt(true);
        void api.trackFunnelEvent('login.prompt.shown', {
          metadata: { reason: 'auth_required' },
        });
        return;
      }

      setIsLoading(true);
      setError('');
      setCurrentPrompt(payload.displayPrompt);

      void api.trackFunnelEvent('prompt.start', {
        userId: user?.id,
        metadata: {
          auth_state: isAuthenticated ? 'authenticated' : 'guest',
          prompt_length: payload.displayPrompt.length,
        },
      });

      const response = await api.sendCompletion(payload.runtimeInput, {
        user: user?.id,
      });
      setResult(response);
      void api.trackFunnelEvent('generation.success', {
        userId: user?.id,
        metadata: {
          auth_state: isAuthenticated ? 'authenticated' : 'guest',
          prompt_length: payload.displayPrompt.length,
        },
      });

      // Track usage
      if (isAuthenticated) {
        incrementUsage();
      } else {
        incrementGuestUsage();
      }

    } catch (err) {
      console.error('Completion error:', err);
      if (isRuntimePaymentRequired(err)) {
        void api.trackFunnelEvent('quota.exhausted', {
          userId: user?.id,
          metadata: {
            auth_state: isAuthenticated ? 'authenticated' : 'guest',
            reason: err.reason || 'runtime_payment_required',
          },
        });
        void api.trackFunnelEvent('paid.gate.shown', {
          userId: user?.id,
          metadata: {
            reason: err.reason || 'runtime_payment_required',
          },
        });
        void api.trackFunnelEvent('runtime.payment_required', {
          userId: user?.id,
          metadata: {
            runtime_reason: err.reason || 'runtime_payment_required',
            checkout_endpoint: err.payment?.checkout_endpoint || '',
            packages_endpoint: err.payment?.packages_endpoint || '',
          },
        });
        if (!isAuthenticated) {
          setShowGuestPrompt(true);
          void api.trackFunnelEvent('login.prompt.shown', {
            metadata: { reason: 'runtime_payment_required' },
          });
        }
        setError(t('paymentRequired'));
        setResult(null);
        return;
      }
      setError(err.message || t('generationFailed'));
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    setResult(null);
    setCurrentPrompt('');
    setError('');
  };

  // Handle copy
  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(String(content).replace(/<[^>]*>/g, ''));
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError(t('copyFailed'));
    }
  };

  // Show loading state during auth initialization
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Guest Prompt Modal */}
      {showGuestPrompt && (
        <GuestPrompt onProceedAsGuest={handleProceedAsGuest} />
      )}

      {/* Error Alert */}
      <ErrorAlert
        message={error}
        onClose={() => setError('')}
      />

      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  {APP_CONFIG.APP_TITLE}
                </h1>
              </div>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                {APP_CONFIG.APP_DESCRIPTION}
              </p>

              {/* Guest mode indicator */}
              {isGuestMode && (
                <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  {t('guestMode')}
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="absolute top-4 right-4">
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Usage meter for authenticated users */}
          {isAuthenticated && billingEnabled && BILLING_CONFIG.UI.SHOW_USAGE_METER && currentPlan === 'free' && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('usageThisMonth')}</h3>
                <span className="text-sm text-gray-500">
                  {quotaInfo.monthlyUsed} / {quotaInfo.monthlyLimit} {t('requests')}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(100, (quotaInfo.monthlyUsed / quotaInfo.monthlyLimit) * 100)}%`
                  }}
                />
              </div>
              {quotaInfo.monthlyRemaining <= 10 && (
                <p className="text-sm text-orange-600 font-medium mt-2">
                  {t('remainingThisMonth', { count: quotaInfo.monthlyRemaining })}
                </p>
              )}
            </div>
          )}

          {/* Input Form */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-shadow duration-300">
            <CompletionForm
              onSubmit={handleSubmit}
              isLoading={isLoading}
              onClear={handleClear}
            />
          </div>

          {/* Result Display */}
          {result && (
            <div className="animate-fade-in-up">
              <ResultDisplay
                result={result}
                prompt={currentPrompt}
                onCopy={handleCopy}
                copied={copied}
              />
            </div>
          )}

          {/* Empty State */}
          {!result && !isLoading && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Sparkles className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                {t('readyTitle')}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {t('readyDescription')}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-20">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center text-sm text-gray-500">
            <p className="flex items-center justify-center gap-2">
              <span>{t('poweredBy')}</span>
              <span className="font-semibold text-blue-600">PromptaaS</span>
            </p>
            {isGuestMode && (
              <p className="mt-2 text-xs">
                {t('guestFooter')}
              </p>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompletionApp;
